import { useNavigate } from "react-router";
import { SurveyListItem } from "../surveys.types";
import VibratingButton from "@/components/base/button/VibratingButton";
import { Check, ClipboardList } from "lucide-react";
import IconChevron from "@/assets/icons/chevron.svg?react";
import IconDonFilledLinear from "@/assets/icons/don-filled-linear.svg?react";
import { useTranslation } from "react-i18next";

type Props = {
  survey: SurveyListItem;
};

export default function SurveyCard({ survey }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const answered = survey.is_completed
    ? survey.questions_count
    : survey.answered_count || 0;
  const total = survey.questions_count || 1;
  const percent = (answered / total) * 100;

  return (
    <VibratingButton
      className="w-full text-start"
      onClick={() => {
        if (!survey.is_completed) {
          navigate(`/profile/surveys/${survey.id}`);
        }
      }}
    >
      <div className="rounded-xl border border-white/[0.08] bg-gray-4 p-3 transition-colors duration-200 active:bg-white/[0.04]">
        <div
          className={`${
            survey.is_completed || answered === 0
              ? "items-center"
              : "items-start"
          } flex gap-3`}
        >
          {/* Icon — brend urg'usi (HomeSurveyCard bilan bir xil til):
              active'da orange tint, tugallanganda green tint */}
          <div
            className={`size-11 rounded-full border flex-center shrink-0 ${
              survey.is_completed
                ? "bg-green-3 border-green-1/20"
                : "bg-orange-100/15 border-orange-100/20"
            }`}
          >
            {survey.is_completed ? (
              <Check className="size-5 text-green" />
            ) : (
              <ClipboardList className="size-5 text-orange-100" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 flex flex-col gap-2">
            {/* Title + count + chevron (active only) */}
            <div className="flex items-center justify-between gap-2">
              <p className="text-[15px] font-semibold text-white leading-130 flex-1">
                {survey.title}
              </p>
              {!survey.is_completed && (
                <div className="flex items-center gap-2 shrink-0">
                  {/* Count only once started — a fresh "0/X" is noise. */}
                  {answered > 0 && (
                    <span className="text-xs font-semibold leading-130 text-orange-100">
                      {answered}/{total}
                    </span>
                  )}
                  {/* Circular chevron — looks/feels like a tappable button:
                      subtle bg + press feedback (darken + scale). */}
                  <span className="size-8 rounded-full bg-white/[0.06] flex-center active:bg-white/[0.14] active:scale-95 transition-all duration-150">
                    <IconChevron
                      className="w-5 h-5 text-gray-1"
                      strokeWidth="1.5"
                    />
                  </span>
                </div>
              )}
            </div>

            {/* Progress bar — only once started (an empty 0% bar is noise) */}
            {!survey.is_completed && answered > 0 && (
              <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all bg-orange-100"
                  style={{ width: `${percent}%` }}
                />
              </div>
            )}

            {/* Completed badge + DON reward */}
            {survey.is_completed && (
              <div className="flex items-center justify-between gap-4">
                <span className="text-xs font-medium text-green leading-130">
                  {t("survey_completed")}
                </span>
                {survey.don_reward > 0 && (
                  <span className="text-xs flex !items-center justify-end gap-1 leading-130">
                    <IconDonFilledLinear width="12" height="12" />
                    <span className="text-orange-100 font-semibold">
                      {Math.floor(survey.don_reward)}
                    </span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </VibratingButton>
  );
}
